import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const KnowledgeBase = ({ onClose, onArticleSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Topics', icon: 'BookOpen' },
    { id: 'occasions', name: 'Occasions', icon: 'Calendar' },
    { id: 'relationships', name: 'Relationships', icon: 'Heart' },
    { id: 'etiquette', name: 'Etiquette', icon: 'Crown' },
    { id: 'budget', name: 'Budget Tips', icon: 'DollarSign' },
    { id: 'cultural', name: 'Cultural Gifts', icon: 'Globe' }
  ];

  const articles = [
    {
      id: 1,
      title: "The Ultimate Guide to Birthday Gifts by Age",
      category: "occasions",
      readTime: "8 min read",
      popularity: 95,
      summary: "Discover age-appropriate gift ideas from toddlers to seniors, with expert insights on developmental preferences and trending items.",
      tags: ["Birthday", "Age Groups", "Popular"]
    },
    {
      id: 2,
      title: "Wedding Gift Etiquette: What to Give and When",
      category: "etiquette",
      readTime: "6 min read",
      popularity: 88,
      summary: "Navigate wedding gift-giving with confidence, including registry guidelines, cash gifts, and timing considerations.",
      tags: ["Wedding", "Etiquette", "Registry"]
    },
    {
      id: 3,
      title: "Meaningful Gifts for Long-Distance Relationships",
      category: "relationships",
      readTime: "5 min read",
      popularity: 92,
      summary: "Bridge the distance with thoughtful gifts that maintain connection and show you care despite the miles.",
      tags: ["Long Distance", "Romance", "Connection"]
    },
    {
      id: 4,
      title: "Corporate Gift Giving: Professional and Appropriate",
      category: "etiquette",
      readTime: "7 min read",
      popularity: 78,
      summary: "Master workplace gift-giving with appropriate choices for colleagues, bosses, and business partners.",
      tags: ["Corporate", "Professional", "Workplace"]
    },
    {
      id: 5,
      title: "Budget-Friendly Gifts That Feel Expensive",
      category: "budget",
      readTime: "10 min read",
      popularity: 96,
      summary: "Create maximum impact with minimal spend through thoughtful presentation and strategic gift selection.",
      tags: ["Budget", "DIY", "Presentation"]
    },
    {
      id: 6,
      title: "Cultural Considerations in Gift Giving",
      category: "cultural",
      readTime: "12 min read",
      popularity: 85,
      summary: "Understand cultural nuances, taboos, and traditions to give respectful and appreciated gifts across cultures.",
      tags: ["Culture", "International", "Traditions"]
    },
    {
      id: 7,
      title: "Holiday Gift Planning: A Month-by-Month Guide",
      category: "occasions",
      readTime: "15 min read",
      popularity: 89,
      summary: "Stay ahead of every holiday with strategic planning, early shopping tips, and seasonal gift trends.",
      tags: ["Holidays", "Planning", "Seasonal"]
    },
    {
      id: 8,
      title: "Gifts for New Parents: Practical and Thoughtful Ideas",
      category: "relationships",
      readTime: "9 min read",
      popularity: 91,
      summary: "Support new parents with gifts that truly help during this transformative time in their lives.",
      tags: ["New Parents", "Baby", "Support"]
    }
  ];

  const filteredArticles = articles.filter(article => {
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  const popularArticles = articles.filter(article => article.popularity >= 90).slice(0, 3);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-accent-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
              <Icon name="BookOpen" size={20} color="white" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-xl text-accent-800">
                Gift Giving Knowledge Base
              </h2>
              <p className="text-sm text-text-secondary">Expert guides and tips for every occasion</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            className="text-accent-500 hover:text-accent-700"
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <Icon 
                  name="Search" 
                  size={20} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400"
                />
                <Input
                  type="search"
                  placeholder="Search guides, tips, and articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Categories */}
            <div className="mb-6">
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-medium text-sm transition-all ${
                      selectedCategory === category.id
                        ? 'bg-primary text-white shadow-warm'
                        : 'bg-surface text-accent-600 hover:bg-primary-50 hover:text-primary'
                    }`}
                  >
                    <Icon 
                      name={category.icon} 
                      size={16} 
                      className={selectedCategory === category.id ? 'text-white' : 'text-accent-500'}
                    />
                    <span>{category.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Popular Articles Section */}
            {searchQuery === '' && selectedCategory === 'all' && (
              <div className="mb-8">
                <h3 className="font-heading font-semibold text-lg text-accent-800 mb-4">
                  Most Popular Guides
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {popularArticles.map((article) => (
                    <div
                      key={article.id}
                      className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-xl p-4 border border-primary-200 cursor-pointer hover:shadow-md transition-all"
                      onClick={() => onArticleSelect(article)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <Icon name="TrendingUp" size={16} className="text-primary" />
                          <span className="text-xs font-medium text-primary">Popular</span>
                        </div>
                        <span className="text-xs text-text-secondary">{article.readTime}</span>
                      </div>
                      <h4 className="font-medium text-accent-800 mb-2 line-clamp-2">{article.title}</h4>
                      <p className="text-sm text-text-secondary line-clamp-2">{article.summary}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Articles Grid */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-lg text-accent-800">
                  {searchQuery ? `Search Results (${filteredArticles.length})` : 'All Articles'}
                </h3>
                <div className="flex items-center space-x-2 text-sm text-text-secondary">
                  <Icon name="Filter" size={16} />
                  <span>Sorted by relevance</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredArticles.map((article) => (
                  <div
                    key={article.id}
                    className="bg-white rounded-xl p-6 border border-accent-200 hover:border-primary-200 hover:shadow-md transition-all cursor-pointer group"
                    onClick={() => onArticleSelect(article)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <Icon 
                          name={categories.find(c => c.id === article.category)?.icon || 'BookOpen'} 
                          size={16} 
                          className="text-secondary"
                        />
                        <span className="text-xs font-medium text-secondary-600 capitalize">
                          {article.category}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-xs text-text-secondary">{article.readTime}</span>
                        <div className="flex items-center space-x-1">
                          <Icon name="Eye" size={12} className="text-text-muted" />
                          <span className="text-xs text-text-muted">{article.popularity}%</span>
                        </div>
                      </div>
                    </div>

                    <h4 className="font-heading font-semibold text-accent-800 mb-2 group-hover:text-primary transition-colors">
                      {article.title}
                    </h4>
                    
                    <p className="text-sm text-text-secondary mb-4 line-clamp-3">
                      {article.summary}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap gap-1">
                        {article.tags.slice(0, 3).map((tag, index) => (
                          <span 
                            key={index}
                            className="px-2 py-1 bg-accent-100 text-accent-600 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <Icon 
                        name="ArrowRight" 
                        size={16} 
                        className="text-accent-400 group-hover:text-primary group-hover:translate-x-1 transition-all"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {filteredArticles.length === 0 && (
                <div className="text-center py-12">
                  <Icon name="Search" size={48} className="text-accent-300 mx-auto mb-4" />
                  <h3 className="font-medium text-accent-600 mb-2">No articles found</h3>
                  <p className="text-text-secondary">Try adjusting your search or browse different categories</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBase;